import React, { useState, useEffect, useRef } from 'react';

interface ProfileSidebarProps {
    isOpen: boolean;
    onClose: () => void;
    session: any;
    profileName: string;
    setProfileName: (name: string) => void;
    profileAddress: string;
    profileCity: string;
    profileState: string;
    profileZipCode: string;
    isEditingProfile: boolean;
    setIsEditingProfile: (status: boolean) => void;
    saveLoading: boolean;
    handleUpdateProfile: (e: React.FormEvent) => void;
    handleLogout: () => void;
    handleDeleteAccountClick: () => void;
    userTreeCount: number;
}

const ProfileSidebar: React.FC<ProfileSidebarProps> = ({
    isOpen, onClose, session, profileName, setProfileName, profileAddress, profileCity, profileState, profileZipCode, isEditingProfile, setIsEditingProfile, saveLoading, handleUpdateProfile, handleLogout, handleDeleteAccountClick, userTreeCount
}) => {
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const wasLoading = useRef(saveLoading);
    
    // Holds the pristine name value before editing starts
    const nameSnapshot = useRef('');

    const handleEditToggle = () => {
        if (!isEditingProfile) {
            // Entering edit mode: Save a snapshot of the current clean name
            nameSnapshot.current = profileName;
            setIsEditingProfile(true);
        } else {
            // Canceling edit mode: Revert state back to the original snapshot
            setProfileName(nameSnapshot.current);
            setIsEditingProfile(false);
        }
    };
    const isNameValid = profileName.trim().length > 0;

    const handleProfileSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!isNameValid) return;
        setProfileName(profileName.trim());
        handleUpdateProfile(e);
    };

    const handleClose = () => {
        if (isEditingProfile) {
            // Revert changes if they close the sidebar while editing
            setProfileName(nameSnapshot.current);
            setIsEditingProfile(false);
        }
        onClose();
    };

    // Automatically trigger custom modal when save operation finishes successfully
    useEffect(() => {
        if (wasLoading.current && !saveLoading && !isEditingProfile) {
            setShowSuccessModal(true);
        }
        wasLoading.current = saveLoading;
    }, [saveLoading, isEditingProfile]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-xs z-[150] flex justify-end">
            <div className="absolute inset-0" onClick={onClose}></div>
            <div className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col justify-between z-10 animate-in slide-in-from-right duration-200 overflow-y-auto">
                <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-green-50 to-emerald-50 relative">
                    <button onClick={handleClose} className="absolute right-4 top-4 text-gray-400 p-1 rounded-full"><svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
                    <div className="flex items-center gap-4 mt-2">
                        <div className="w-16 h-16 rounded-full bg-green-600 text-white font-black text-2xl flex items-center justify-center shadow-md">{session?.user?.email?.substring(0, 2).toUpperCase()}</div>
                        <div className="text-left">
                            <h3 className="text-xl font-bold text-gray-900 tracking-tight">{profileName || 'MitiFresh User'}</h3>
                            <p className="text-xs font-semibold text-green-700">Active Garden Member</p>
                        </div>
                    </div>
                </div>

                <div className="p-6 flex-grow space-y-6 text-left">
                    <div>
                        <div className="flex justify-between items-center mb-3">
                            <h4 className="text-sm font-bold text-gray-400 tracking-wider uppercase">Account Details</h4>
                            <button onClick={handleEditToggle} className="text-xs text-green-600 font-bold hover:underline">
                                {isEditingProfile ? "Cancel" : "Edit"}
                            </button>
                        </div>
                        {isEditingProfile ? (
                            <form onSubmit={handleProfileSubmit} className="space-y-3 bg-gray-50 p-3 rounded-2xl border border-gray-100">
                                <div><label className="block text-xs font-semibold text-gray-600 mb-1">Full Name</label><input type="text" value={profileName} onChange={(e) => setProfileName(e.target.value)} className="w-full text-sm px-3 py-1.5 border rounded-lg text-black bg-white outline-none" required /></div>
                                <div><label className="block text-xs font-semibold text-gray-600 mb-1">Street Address</label><input type="text" value={profileAddress} disabled className="w-full text-sm px-3 py-1.5 border rounded-lg text-gray-500 bg-gray-100 cursor-not-allowed opacity-60" /></div>
                                <div><label className="block text-xs font-semibold text-gray-600 mb-1">City</label><input type="text" value={profileCity} disabled className="w-full text-sm px-3 py-1.5 border rounded-lg text-gray-500 bg-gray-100 cursor-not-allowed opacity-60" /></div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div><label className="block text-xs font-semibold text-gray-600 mb-1">State</label><input type="text" value={profileState} disabled className="w-full text-sm px-3 py-1.5 border rounded-lg text-gray-500 bg-gray-100 cursor-not-allowed opacity-60" /></div>
                                    <div><label className="block text-xs font-semibold text-gray-600 mb-1">ZIP Code</label><input type="text" value={profileZipCode} disabled className="w-full text-sm px-3 py-1.5 border rounded-lg text-gray-500 bg-gray-100 cursor-not-allowed opacity-60" /></div>
                                </div>
                                <button type="submit" disabled={saveLoading || !isNameValid} className="w-full bg-green-600 text-white text-xs font-bold py-2 rounded-lg hover:bg-green-700 disabled:opacity-50">{saveLoading ? "Saving..." : "Save Details"}</button>
                            </form>
                        ) : (
                            <div className="space-y-2 text-sm font-medium text-gray-800 bg-gray-50/50 p-3 rounded-2xl border border-gray-100/60">
                                <p><span className="text-gray-400 font-normal">Email:</span> {session?.user?.email}</p>
                                <p><span className="text-gray-400 font-normal">Address:</span> {profileAddress ? `${profileAddress}, ${profileCity}, ${profileState} ${profileZipCode}` : <span className="text-amber-600 text-xs font-semibold">⚠️ No address set yet</span>}</p>
                                <p><span className="text-gray-400 font-normal">Active Trees Listed:</span> <span className="font-bold text-green-600">{userTreeCount} active</span></p>
                            </div>
                        )}
                    </div>
                    <div className="bg-white rounded-3xl p-3 border border-gray-100 shadow-sm flex flex-col">
                        <h4 className="text-sm font-bold text-green-700 tracking-tight px-3 pt-2 pb-3 border-b border-gray-100" style={{ fontFamily: 'Comic Sans MS, Comic Sans, cursive' }}>Settings</h4>
                        <button className="w-full flex items-center justify-between px-3 py-3.5 text-gray-700 font-medium text-sm hover:bg-gray-50/80 rounded-xl group">
                            <div className="flex items-center gap-3">
                                <svg className="w-5 h-5 text-gray-500 group-hover:scale-105 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>
                                <span>Notifications</span>
                            </div>
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                        </button>
                        <button className="w-full flex items-center justify-between px-3 py-3.5 text-gray-700 font-medium text-sm hover:bg-gray-50/80 rounded-xl group">
                            <div className="flex items-center gap-3">
                                <svg className="w-5 h-5 text-gray-500 group-hover:scale-105 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                                <span>Privacy</span>
                            </div>
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                        </button>
                        <button onClick={handleLogout} className="w-full flex items-center justify-between px-3 py-3.5 text-red-600 font-medium text-sm hover:bg-red-50/30 rounded-xl group">
                            <div className="flex items-center gap-3">
                                <svg className="w-5 h-5 text-red-500 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a1 1 0 011 1v1" /></svg>
                                <span>Logout</span>
                            </div>
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                        </button>
                        <button onClick={handleDeleteAccountClick} className="w-full flex items-center justify-between px-3 py-3.5 text-red-600 font-medium text-sm hover:bg-red-50/30 rounded-xl group">
                            <div className="flex items-center gap-3">
                                <svg className="w-5 h-5 text-red-500 group-hover:animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                <span>Delete Account</span>
                            </div>
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                        </button>
                    </div>
                </div>

                {/* Custom Profile Update Success Modal */}
                {showSuccessModal && (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-[200] p-4 animate-in fade-in duration-100">
                        <div className="bg-[#333333] text-white rounded-xl p-6 max-w-xs w-full shadow-2xl text-left border border-gray-700/50 animate-in zoom-in-95 duration-150">
                            <h3 className="text-xl font-semibold mb-2 text-white" style={{ fontFamily: 'Comic Sans MS, Comic Sans, cursive' }}>
                                Success
                            </h3>
                            <p className="text-gray-200 text-sm mb-6 leading-relaxed" style={{ fontFamily: 'Comic Sans MS, Comic Sans, cursive' }}>
                                Profile updated successfully
                            </p>
                            <div className="text-right">
                                <button 
                                    type="button" 
                                    onClick={() => setShowSuccessModal(false)} 
                                    className="text-emerald-400 font-bold hover:text-emerald-300 px-3 py-1 transition-colors text-sm uppercase tracking-wider"
                                    style={{ fontFamily: 'Comic Sans MS, Comic Sans, cursive' }}
                                >
                                    OK
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProfileSidebar;